import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ExternalLink, FileText } from 'lucide-react';

import Header from '../components/Header';
import Footer from '../components/Footer';
import FloatingButtons from '../components/FloatingButtons';
import { useReveal } from '../hooks/useReveal';

const students = [
  {
    slNo: 1,
    formNo: 'AKTU 202686017381',
    name: 'ABDULLAH SHAHID',
    fatherName: 'MOHD SHAHID JAMAL',
    percentage: '67.76%',
    meritNo: 1,
  },
  {
    slNo: 2,
    formNo: 'AKTU 202686016967',
    name: 'MANGESH SAROJ',
    fatherName: 'RAMNATH SAROJ',
    percentage: '67.65%',
    meritNo: 2,
  },
];

function StudentRow({ student, index }) {
  const { ref, inView } = useReveal();

  return (
    <tr
      ref={ref}
      style={{ transitionDelay: `${index * 120}ms` }}
      className={`reveal ${inView ? 'in-view' : ''} border-b border-slate-200 last:border-b-0 hover:bg-[var(--pca-blue-soft)]/30 transition-colors`}
    >
      <td className="px-4 py-5 text-center font-semibold text-slate-700">
        {student.slNo}
      </td>

      <td className="px-4 py-5 text-sm font-medium text-slate-700 whitespace-nowrap">
        {student.formNo}
      </td>

      <td className="px-4 py-5 text-sm font-bold text-[#0B2E6E] whitespace-nowrap">
        {student.name}
      </td>

      <td className="px-4 py-5 text-sm text-slate-700 whitespace-nowrap">
        {student.fatherName}
      </td>

      <td className="px-4 py-5 text-center text-sm font-semibold text-slate-700">
        {student.percentage}
      </td>

      <td className="px-4 py-5 text-center">
        <span className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-[var(--pca-blue-soft)] text-[#0B2E6E] font-extrabold">
          {student.meritNo}
        </span>
      </td>
    </tr>
  );
}

export default function BPharmLateralEntry() {
  const { ref: heroRef, inView: heroInView } = useReveal();
  const { ref: noticeRef, inView: noticeInView } = useReveal();

  return (
    <div className="min-h-screen bg-white">
      <Header />

      <main>
        {/* Hero */}
        <section className="relative overflow-hidden bg-[#0B2E6E] text-white">
          {/* Soft background decoration */}
          <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-white/5" />
          <div className="absolute -bottom-32 -left-20 w-80 h-80 rounded-full bg-white/5" />

          <div
            ref={heroRef}
            className={`container-x relative py-16 md:py-20 reveal ${
              heroInView ? 'in-view' : ''
            }`}
          >
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-white/75 hover:text-white text-sm font-medium transition-colors"
            >
              <ArrowLeft size={16} />
              Back to Home
            </Link>

            <div className="mt-8 max-w-3xl">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 border border-white/10 text-xs font-semibold tracking-wide">
                <FileText size={14} />
                OFFICIAL ADMISSION NOTICE
              </div>

              <h1 className="mt-5 text-3xl md:text-5xl font-extrabold leading-tight">
                B.Pharm Lateral Entry
              </h1>

              <p className="mt-3 text-xl md:text-2xl text-white/85 font-medium">
                Second Year Admission
              </p>

              <div className="mt-5 inline-flex px-4 py-2 rounded-full bg-white/10 text-sm font-semibold">
                Academic Session 2026–27
              </div>
            </div>
          </div>
        </section>

        {/* Main Notice */}
        <section className="py-12 md:py-20 bg-slate-50">
          <div className="container-x">
            <div
              ref={noticeRef}
              className={`max-w-6xl mx-auto reveal ${
                noticeInView ? 'in-view' : ''
              }`}
            >
              <div className="bg-white rounded-2xl md:rounded-3xl border border-[var(--pca-border)] shadow-[0_20px_50px_-30px_rgba(11,46,110,0.35)] overflow-hidden">
                {/* Notice heading */}
                <div className="p-6 md:p-10 text-center border-b border-slate-100">
                  <span className="section-eyebrow">
                    B.PHARM. LATERAL ENTRY
                  </span>

                  <h2 className="mt-4 text-2xl md:text-3xl font-extrabold text-[#0B2E6E] leading-tight">
                    Application Received for B.Pharm. (Lateral Entry)
                    <br className="hidden md:block" />
                    Second Year Admission Against Vacant Seat & Merit List
                  </h2>

                  <p className="mt-3 text-slate-500 text-sm">
                    Academic Session 2026–27
                  </p>
                </div>

                {/* Table */}
                <div className="p-4 md:p-8">
                  <div className="overflow-x-auto rounded-xl border border-slate-200">
                    <table className="w-full min-w-[950px] border-collapse">
                      <thead>
                        <tr className="bg-[#0B2E6E] text-white">
                          <th className="border-r border-white/20 px-4 py-4 text-center text-sm font-bold">
                            Sl. No.
                          </th>

                          <th className="border-r border-white/20 px-4 py-4 text-left text-sm font-bold">
                            Form No.
                          </th>

                          <th className="border-r border-white/20 px-4 py-4 text-left text-sm font-bold">
                            Name of Candidate
                          </th>

                          <th className="border-r border-white/20 px-4 py-4 text-left text-sm font-bold">
                            Father's Name
                          </th>

                          <th className="border-r border-white/20 px-4 py-4 text-center text-sm font-bold">
                            Obtained Percentage
                            <br />
                            in Qualifying Exam
                          </th>

                          <th className="px-4 py-4 text-center text-sm font-bold">
                            Merit No.
                          </th>
                        </tr>
                      </thead>

                      <tbody>
                        {students.map((student, index) => (
                          <StudentRow
                            key={student.slNo}
                            student={student}
                            index={index}
                          />
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Bottom information */}
                  <div className="mt-8 flex flex-col md:flex-row items-center justify-between gap-5 border-t border-slate-200 pt-6">
                    <div className="text-sm text-slate-600">
                      <span className="font-semibold text-slate-800">
                        Date:
                      </span>{' '}
                      14.09.2026
                    </div>

                    <a
                      href="/lateral-entry-2026-27.jpg"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="group inline-flex items-center justify-center gap-2 rounded-xl bg-[#0B2E6E] px-6 py-3 text-sm font-bold text-white shadow-[0_10px_25px_-12px_rgba(11,46,110,0.8)] hover:-translate-y-0.5 hover:bg-[#082354] transition-all duration-300"
                    >
                      View Original Document
                      <ExternalLink
                        size={16}
                        className="group-hover:translate-x-0.5 transition-transform"
                      />
                    </a>
                  </div>
                </div>
              </div>

              {/* Verification note */}
              <div className="mt-6 text-center">
                <p className="text-xs md:text-sm text-slate-500">
                  The above information is published for official reference.
                  Please refer to the original document for verification.
                </p>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
      <FloatingButtons />
    </div>
  );
}